import dayjs from 'dayjs';
import { useEffect } from 'react';

/**
 * Хүргэлтийн захиалга үүсгэх урсгалын хагас дутуу мэдээллийг (draft) хадгалах.
 *
 * Бүх төлөв нь React state-д байдаг тул хуудсаа refresh хийхэд бөглөсөн
 * мэдээлэл алга болдог байсан. sessionStorage-д хадгалснаар refresh хийсэн ч
 * сонгосон бараа, хаяг, формын утгууд сэргэнэ.
 *
 * Аль алхам дээр байгааг нь URL-ийн `?step=` параметр агуулна (Add.js) —
 * ингэснээр refresh, browser-ийн back/forward хоёулаа ажиллана.
 *
 * ХАМРАХ ХҮРЭЭ: draft нь ЗӨВХӨН тухайн хуудас дээр байх хугацаанд л
 * хүчинтэй. Өөр хуудас руу орвол `useClearDraftOnLeave` шууд цэвэрлэдэг тул
 * дараа дахин орж ирэхэд форм хоосон эхэлнэ (доорх hook-ийг үзнэ үү).
 *
 * sessionStorage-ыг сонгосон шалтгаан: таб хаагдахад өөрөө цэвэрлэгдэнэ,
 * өөр таб дээрх шинэ захиалгад хөндлөнгөөс оролцохгүй.
 *
 * ЧУХАЛ: `AddOrderDeliveryProvider`-ыг шуурхай хүргэлтийн урсгал
 * (orderExpressDelivery/Add) ч хуваалцаж хэрэглэдэг. Тиймээс key-г урсгал тус
 * бүрд өгнө — key дамжуулаагүй бол draft бүрэн унтарна (хоёр урсгалын мэдээлэл
 * хоорондоо хутгалдахгүй).
 */
export const DELIVERY_DRAFT_KEY = 'add_order_delivery_draft';

/**
 * Draft-ын key-г merchant тус бүрд салгана.
 *
 * `changeMerchant` (store/createUserSlice.js) нь merchant солиход
 * `window.location.href`-ээр НЭГ ЛЭ таб дотор дахин уншуулдаг тул sessionStorage
 * хэвээрээ үлддэг. Key нь тогтмол байсан үед өмнөх merchant дээр сонгосон
 * агуулах (жиш нь CNN) шинэ merchant-ийн формд сэргэж, тухайн merchant-д
 * байхгүй агуулах Select дээр хадаад үлддэг байсан.
 */
export const deliveryDraftKey = (merchantCode) =>
  merchantCode ? `${DELIVERY_DRAFT_KEY}_${merchantCode}` : DELIVERY_DRAFT_KEY;

/** JSON болгоход dayjs/moment объектууд мөр болдог тул сэргээж эргүүлнэ */
const reviveDate = (value) => (value ? dayjs(value) : value);

/** Формын огноо/цагийн талбаруудыг dayjs болгож эргүүлнэ */
const reviveFormValues = (values) => {
  if (!values) return values;

  return {
    ...values,
    ...(values.dropoffDate ? { dropoffDate: reviveDate(values.dropoffDate) } : {}),
    ...(Array.isArray(values.dropoffTime)
      ? { dropoffTime: values.dropoffTime.map(reviveDate) }
      : {}),
  };
};

const reviveDraft = (draft) => ({
  ...draft,
  ...(draft?.formDataStep2 ? { formDataStep2: reviveFormValues(draft.formDataStep2) } : {}),
  ...(draft?.formValuesStep2 ? { formValuesStep2: reviveFormValues(draft.formValuesStep2) } : {}),
});

export const readDraft = (key) => {
  if (!key) return {};

  try {
    const raw = sessionStorage.getItem(key);
    return raw ? reviveDraft(JSON.parse(raw)) : {};
  } catch (error) {
    console.error('readDraft error: ', error);
    return {};
  }
};

export const writeDraft = (key, draft) => {
  if (!key) return;

  try {
    sessionStorage.setItem(key, JSON.stringify(draft));
  } catch (error) {
    // Хэмжээ хэтэрсэн зэрэг тохиолдолд урсгалыг тасалдуулахгүй
    console.error('writeDraft error: ', error);
  }
};

/** Захиалга амжилттай үүссэний дараа (Step3) дуудаж draft-ыг цэвэрлэнэ */
export const clearDraft = (key) => {
  if (!key) return;

  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error('clearDraft error: ', error);
  }
};

/**
 * Хуудсаас гармагц draft-ыг цэвэрлэнэ.
 *
 * Захиалга үүсгэх хуудас unmount болох (өөр хуудас руу орох, гарчгийн "back",
 * breadcrumb, хажуугийн меню, browser-ийн back — бүгд) үед л cleanup ажиллана.
 * Тиймээс хагас бөглөсөн мэдээлэл дараа хуудсанд дахин орж ирэхэд сэргэхгүй.
 *
 * Refresh дээр ХАМААРАХГҮЙ: хуудас дахин уншихад JS бүхэлдээ унтардаг тул
 * React cleanup ажиллаж чаддаггүй — refresh-ийн сэргээлт хэвээрээ.
 *
 * Алхам солих (`?step=`) нь зөвхөн search-ийг өөрчилдөг, route нэг хэвээрээ
 * тул unmount болохгүй — алхам хооронд draft хадгалагдана.
 *
 * `key` нь merchant-аар өөрчлөгддөг (`deliveryDraftKey`) тул merchant солиход
 * өмнөх merchant-ийн draft мөн адил цэвэрлэгдэнэ.
 */
export const useClearDraftOnLeave = (key) => {
  useEffect(() => () => clearDraft(key), [key]);
};
